import React, { useEffect, useRef, useState } from "react";
import { Pencil, Minus, ArrowUpDown, MoveHorizontal, Square, TrendingUp, Waves, Split, Trash2, X, Ban, Slash, MoveDiagonal, MousePointer, Circle, Triangle, ArrowRight, Ruler } from "lucide-react";

/**
 * Drawing tools menu — uses klinecharts native overlay primitives.
 * Simplicity-first UX: click a tool → chart enters draw mode (user clicks on chart to place points).
 */

const CATEGORIES = [
  { title: "Lignes", tools: [
    { name: "segment",                label: "Ligne",             icon: Minus,          desc: "Trait entre 2 points" },
    { name: "rayLine",                label: "Rayon",             icon: ArrowRight,     desc: "Demi-droite" },
    { name: "straightLine",           label: "Droite",            icon: Slash,          desc: "Ligne infinie" },
    { name: "horizontalStraightLine", label: "Horizontale",       icon: MoveHorizontal, desc: "Niveau de prix" },
    { name: "verticalStraightLine",   label: "Verticale",         icon: ArrowUpDown,    desc: "Événement temporel" },
    { name: "horizontalRayLine",      label: "Rayon H.",          icon: MoveHorizontal, desc: "Support/résistance à droite" },
    { name: "verticalRayLine",        label: "Rayon V.",          icon: ArrowUpDown,    desc: "Rayon vertical" },
    { name: "horizontalSegment",      label: "Segment H.",        icon: MoveHorizontal, desc: "Niveau borné" },
    { name: "priceLine",              label: "Ligne de prix",     icon: Ruler,          desc: "Niveau avec label" },
  ]},
  { title: "Formes", tools: [
    { name: "rect",                   label: "Rectangle",         icon: Square,         desc: "Zone d'intérêt" },
    { name: "circle",                 label: "Cercle",            icon: Circle,         desc: "Zone circulaire" },
    { name: "triangle",               label: "Triangle",          icon: Triangle,       desc: "Triangle 3-points" },
    { name: "arrow",                  label: "Flèche",            icon: ArrowRight,     desc: "Direction/annotation" },
  ]},
  { title: "Analyses avancées", tools: [
    { name: "fibonacciLine",          label: "Fib Retracement",   icon: TrendingUp,     desc: "0 · 23.6 · 38.2 · 50 · 61.8 · 78.6 · 100" },
    { name: "fibonacciCircle",        label: "Fib Cercle",        icon: Circle,         desc: "Cercles concentriques" },
    { name: "fibonacciSegment",       label: "Fib Segment",       icon: TrendingUp,     desc: "Fibonacci sur segment" },
    { name: "fibonacciSpiral",        label: "Fib Spirale",       icon: TrendingUp,     desc: "Spirale d'or" },
    { name: "fibonacciSpeedResistanceFan", label: "Fib Éventail", icon: TrendingUp,     desc: "Éventail de résistances" },
    { name: "fibonacciExtension",     label: "Fib Extension",     icon: TrendingUp,     desc: "127.2 · 161.8 · 261.8" },
    { name: "gannBox",                label: "Gann Box",          icon: Square,         desc: "Boîte de Gann" },
    { name: "priceChannelLine",       label: "Canal parallèle",   icon: Split,          desc: "Deux droites équidistantes" },
    { name: "parallelStraightLine",   label: "Droites parallèles",icon: Split,          desc: "N droites équidistantes" },
  ]},
  { title: "Vagues Elliott / patterns", tools: [
    { name: "threeWaves",             label: "Elliott 3 vagues",  icon: Waves,          desc: "Correction A-B-C" },
    { name: "fiveWaves",              label: "Elliott 5 vagues",  icon: Waves,          desc: "Impulsion 1-2-3-4-5" },
    { name: "eightWaves",             label: "Elliott 8 vagues",  icon: Waves,          desc: "Cycle complet 1-5+ABC" },
    { name: "abcd",                   label: "Pattern ABCD",      icon: MoveDiagonal,   desc: "Harmonique 4-points" },
    { name: "xabcd",                  label: "Pattern XABCD",     icon: MoveDiagonal,   desc: "Harmonique 5-points" },
  ]},
];

export default function DrawingMenu({ getChart, autoDetectFib, isAutoDetectAvailable }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const startDrawing = (toolName) => {
    const chart = getChart?.();
    if (!chart) return;
    try {
      chart.createOverlay({ name: toolName, groupId: "user-draw" });
    } catch { /* noop */ }
    setOpen(false);
  };

  const clearAll = () => {
    const chart = getChart?.();
    if (!chart) return;
    try {
      chart.removeOverlay({ groupId: "user-draw" });
    } catch { /* noop */ }
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="chip text-xs flex items-center gap-1.5 hover:bg-white/5"
        data-testid="chart-draw-btn"
        title="Outils de dessin (Fibonacci, Elliott, canaux, lignes)"
      >
        <Pencil size={12} /> Dessin
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-[60] panel p-3 w-80 max-h-[70vh] flex flex-col" data-testid="chart-draw-menu">
          <div className="flex items-center justify-between mb-2 shrink-0">
            <div className="text-xs font-semibold uppercase tracking-widest text-white/60">Outils de dessin</div>
            <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white" aria-label="Fermer"><X size={13} /></button>
          </div>
          <div className="overflow-y-auto flex-1 space-y-3 pr-1 -mr-1">
            {CATEGORIES.map((cat) => (
              <div key={cat.title}>
                <div className="text-[9px] font-semibold uppercase tracking-widest text-white/40 mb-1.5">{cat.title}</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {cat.tools.map((t) => {
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.name}
                        onClick={() => startDrawing(t.name)}
                        className="flex items-start gap-1.5 px-2 py-1.5 rounded-md border border-white/10 hover:border-accent-blue hover:bg-white/5 text-left"
                        data-testid={`draw-tool-${t.name}`}
                        title={t.desc}
                      >
                        <Icon size={13} className="mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-[10px] font-semibold truncate">{t.label}</div>
                          <div className="text-[8px] text-white/40 truncate">{t.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="shrink-0 pt-2 mt-2 border-t border-white/5 space-y-1">
            {isAutoDetectAvailable && (
              <button
                onClick={() => { autoDetectFib?.(); setOpen(false); }}
                className="w-full text-[11px] py-1.5 rounded-md border border-accent-blue/40 bg-accent-blue/10 text-accent-blue hover:bg-accent-blue/20 flex items-center justify-center gap-1.5"
                data-testid="draw-auto-fib"
              >
                <TrendingUp size={11} /> Auto-détecter Fibonacci (dernier swing)
              </button>
            )}
            <button
              onClick={clearAll}
              className="w-full text-[10px] py-1.5 rounded-md border border-red-500/20 hover:border-red-500/50 hover:bg-red-500/10 flex items-center justify-center gap-1.5 text-red-300"
              data-testid="draw-clear-all"
            >
              <Trash2 size={11} /> Effacer tous les dessins
            </button>
            <div className="text-[9px] text-white/40 mt-2 flex items-start gap-1">
              <Ban size={9} className="mt-0.5 shrink-0" />
              <span>Clic sur un outil, puis clic sur le graphique pour placer les points. Clic-droit sur un dessin pour le supprimer.</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
